import { useMemo } from 'react'
import { createAvatar, type Style } from '@dicebear/core'
import { thumbs, initials, botttsNeutral } from '@dicebear/collection'
import { cn } from '@/lib/utils'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const STYLES: Record<string, Style<any>> = { thumbs, initials, botttsNeutral }

export type AvatarPlaceholderVariant = keyof typeof STYLES

function hashCode(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
  return hash
}

function hslToHex(h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color).toString(16).padStart(2, '0')
  }
  return `${f(0)}${f(8)}${f(4)}`
}

function pastelHex(seed: string, hueOffset = 0, saturation = 0.7, lightness = 0.85): string {
  const hash = hashCode(seed)
  const hue = (Math.abs(hash) + hueOffset) % 360
  return hslToHex(hue, saturation, lightness)
}

interface AvatarPlaceholderProps {
  seed: string
  size?: number
  variant?: AvatarPlaceholderVariant
  className?: string
}

export function AvatarPlaceholder({
  seed,
  size = 36,
  variant = 'thumbs',
  className,
}: AvatarPlaceholderProps) {
  const dataUri = useMemo(() => {
    const backgroundColor = pastelHex(seed)
    const avatar = createAvatar(STYLES[variant], {
      seed,
      size: 128,
      backgroundColor: [backgroundColor],
      ...(variant === 'thumbs' ? {
        shapeColor: [pastelHex(seed, 180, 0.55, 0.78)],
      } : {}),
    })
    return avatar.toDataUri()
  }, [seed, variant])

  return (
    <img
      src={dataUri}
      alt=""
      className={cn('rounded-full', className)}
      style={{ width: size, height: size }}
    />
  )
}
